/** Employee month — calendar, day detail, fixes, undo, roster and profile (spec §4.1 screen 3, D3, D4, D6). */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, RefreshCw, Undo2, Wrench, CalendarRange, UserCog } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import ReasonDialog from './ReasonDialog';
import { ACTION, STATUS, SUGGESTION, api, clock, employeeName, errorText, hm, label, thisMonth } from './shared';

const WEEKDAYS = { en: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'], ar: ['أحد', 'إثنين', 'ثلاثاء', 'أربعاء', 'خميس', 'جمعة', 'سبت'] };
const FIX_KINDS = ['add_punch', 'ignore_punch', 'set_status', 'split', 'half_day_leave', 'half_day_unexcused',
  'confirm_overtime', 'approve_overtime'];
const FIX_LABEL = {
  add_punch: { en: 'Add a punch', ar: 'إضافة بصمة' }, ignore_punch: { en: 'Ignore a punch', ar: 'تجاهل بصمة' },
  set_status: { en: 'Set the day status', ar: 'تحديد حالة اليوم' }, split: { en: 'Split at a break', ar: 'تقسيم عند استراحة' },
  half_day_leave: { en: 'Half-day leave', ar: 'نصف يوم إجازة' }, half_day_unexcused: { en: 'Half-day unexcused', ar: 'نصف يوم بدون عذر' },
  confirm_overtime: { en: 'Confirm overtime', ar: 'اعتماد الإضافي' }, approve_overtime: { en: 'Approve overtime (minutes)', ar: 'اعتماد إضافي (دقائق)' },
};
const SET_STATUSES = ['absent_unexcused', 'absent_excused', 'off', 'paid_day_off', 'present'];
const PROFILES = {
  two_shift_production: { en: 'Two-shift production (10 h)', ar: 'إنتاج بفترتين (10 ساعات)' },
  driver: { en: 'Driver (one long shift)', ar: 'سائق (فترة طويلة واحدة)' },
  office: { en: 'Office (8 h, overtime pre-approved)', ar: 'مكتبي (8 ساعات، إضافي بموافقة مسبقة)' },
};

export function PersonPicker({ token, isRTL, value, onChange }) {
  const t = (en, ar) => (isRTL ? ar : en);
  const [q, setQ] = useState('');
  const [people, setPeople] = useState([]);
  useEffect(() => {
    const h = setTimeout(() => {
      api(token).get('/people', q ? { search: q } : {}).then((r) => setPeople(r.items)).catch(() => setPeople([]));
    }, 250);
    return () => clearTimeout(h);
  }, [token, q]);
  return (
    <div className="flex gap-2 items-center">
      <Input placeholder={t('Search name or number', 'ابحث بالاسم أو الرقم')} value={q} onChange={(e) => setQ(e.target.value)}
        className="w-56" data-testid="month-person-search" />
      <select className="border rounded-md px-3 py-2 text-sm bg-white max-w-xs" value={value || ''}
        onChange={(e) => onChange(e.target.value)} data-testid="month-person-select">
        <option value="">{t('Choose an employee…', 'اختر موظفاً…')}</option>
        {people.map((p) => (
          <option key={p.id} value={p.id}>{employeeName(p, isRTL)}{p.employee_number ? ` (${p.employee_number})` : ''}</option>
        ))}
      </select>
    </div>
  );
}

export default function EmployeeMonthTab({ token, isRTL, access, employeeId, month, onSelect }) {
  const t = (en, ar) => (isRTL ? ar : en);
  const m = month || thisMonth();
  const [data, setData] = useState(null);
  const [adjustments, setAdjustments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [openDay, setOpenDay] = useState(null);
  const [fix, setFix] = useState(null);           // {kind, time, status, break_start, break_end, minutes, next_day}
  const [undoing, setUndoing] = useState(null);
  const [rosterMode, setRosterMode] = useState(false);
  const [rosterChanges, setRosterChanges] = useState({});
  const [rosterSave, setRosterSave] = useState(false);
  const [profile, setProfile] = useState(null);

  const load = useCallback(async () => {
    if (!employeeId) return;
    setLoading(true); setError('');
    try {
      const [d, a] = await Promise.all([
        api(token).get(`/employees/${employeeId}/days`, { month: m }),
        api(token).get(`/employees/${employeeId}/adjustments`, { month: m }),
      ]);
      setData(d); setAdjustments(a.items); setRosterChanges({});
    } catch (e) { setError(errorText(e)); setData(null); } finally { setLoading(false); }
  }, [token, employeeId, m]);
  useEffect(() => { load(); }, [load]);

  const days = data?.days || [];
  const lead = days.length ? new Date(`${days[0].day}T00:00:00`).getDay() : 0;
  const sugByDay = useMemo(() => (data?.suggestions || []).reduce((a, s) => ({ ...a, [s.day]: [...(a[s.day] || []), s] }), {}), [data]);
  const day = days.find((d) => d.day === openDay);
  const dayAdjustments = adjustments.filter((a) => a.day === openDay);
  const sum = data?.summary;

  const clickDay = (d) => {
    if (!rosterMode) { setOpenDay(d.day); return; }
    const current = rosterChanges[d.day] !== undefined ? rosterChanges[d.day] : (d.day_type === 'off' ? 'off' : 'work');
    setRosterChanges({ ...rosterChanges, [d.day]: current === 'off' ? 'work' : 'off' });
  };

  const saveFix = async (reason) => {
    const body = { kind: fix.kind, reason };
    ['time', 'status', 'break_start', 'break_end'].forEach((k) => { if (fix[k]) body[k] = fix[k]; });
    if (fix.minutes) body.minutes = Number(fix.minutes);
    if (fix.next_day) body.next_day = true;
    try {
      await api(token).post(`/employees/${employeeId}/days/${openDay}/adjustments`, body);
      toast.success(t('Day updated', 'تم تحديث اليوم'));
      setFix(null); load();
    } catch (e) { toast.error(errorText(e)); }
  };
  const undo = async (reason) => {
    try {
      await api(token).post(`/adjustments/${undoing.id}/undo`, { reason });
      toast.success(t('Change undone', 'تم التراجع'));
      setUndoing(null); load();
    } catch (e) { toast.error(errorText(e)); }
  };
  const saveRoster = async (reason) => {
    try {
      await api(token).put('/rosters', { employee_id: employeeId, days: rosterChanges, reason });
      toast.success(t('Roster saved', 'تم حفظ جدول الراحات'));
      setRosterSave(false); setRosterMode(false); load();
    } catch (e) { toast.error(errorText(e)); }
  };
  const saveProfile = async (reason) => {
    const shifts = profile.shifts.filter(([a, b]) => a && b);
    try {
      await api(token).put(`/profiles/${employeeId}`, { profile_key: profile.key, expected_shifts: shifts.length ? shifts : null, reason });
      toast.success(t('Profile saved', 'تم حفظ الملف'));
      setProfile(null); load();
    } catch (e) { toast.error(errorText(e)); }
  };

  return (
    <div className="space-y-4 mt-4" data-testid="attendance-employee-month">
      <div className="flex flex-wrap items-center gap-2">
        <PersonPicker token={token} isRTL={isRTL} value={employeeId} onChange={(id) => onSelect(id, m)} />
        <Input type="month" value={m} onChange={(e) => onSelect(employeeId, e.target.value)} className="w-40" data-testid="month-input" />
        {data?.period && (
          <span className="text-xs text-slate-500" data-testid="month-period">
            {isRTL ? `فترة الراتب ${data.period.start} – ${data.period.end}` : `Pay period ${data.period.start} – ${data.period.end}`}
          </span>
        )}
        <Button variant="outline" size="sm" onClick={load} disabled={loading || !employeeId}>
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
        </Button>
        {employeeId && (
          <div className="flex gap-2 ms-auto">
            <Button size="sm" variant={rosterMode ? 'default' : 'outline'} onClick={() => { setRosterMode(!rosterMode); setRosterChanges({}); }}
              data-testid="month-roster-mode">
              <CalendarRange className="h-4 w-4 me-1" />{t('Edit off days', 'تعديل أيام الراحة')}
            </Button>
            {access?.hr_scope && (
              <Button size="sm" variant="outline" onClick={() => setProfile({ key: 'two_shift_production', shifts: [['', ''], ['', '']] })}
                data-testid="month-profile">
                <UserCog className="h-4 w-4 me-1" />{t('Work profile', 'ملف الدوام')}
              </Button>
            )}
          </div>
        )}
      </div>

      {!employeeId && <div className="brand-empty-state p-8 text-center text-slate-500">{t('Choose an employee.', 'اختر موظفاً.')}</div>}
      {error && <div className="text-sm text-rose-600">{error}</div>}

      {sum && (
        <div className="grid grid-cols-2 md:grid-cols-6 gap-2 text-sm" data-testid="month-summary">
          {[
            [t('Worked', 'ساعات العمل'), hm(sum.worked_minutes)],
            [t('Overtime', 'الإضافي'), hm(sum.overtime_minutes)],
            [t('Payable OT (×1.5)', 'إضافي مستحق (×1.5)'), `${sum.payable_overtime_hours} ${t('h', 'س')}`],
            [t('Absent (unexcused)', 'غياب بدون عذر'), sum.absent_unexcused_days],
            [t('Late', 'التأخير'), hm(sum.late_minutes)],
            [t('Needs review', 'تحتاج مراجعة'), sum.needs_review_days],
          ].map(([k, v]) => (
            <Card key={k}><CardContent className="py-2"><div className="text-xs text-slate-500">{k}</div><div className="font-bold">{v}</div></CardContent></Card>
          ))}
        </div>
      )}

      {rosterMode && (
        <div className="flex items-center gap-2 text-sm bg-yellow-50 border border-yellow-200 rounded-md p-2">
          {t('Click days to switch between work and off.', 'انقر على الأيام للتبديل بين عمل وراحة.')}
          <Button size="sm" className="ms-auto" disabled={!Object.keys(rosterChanges).length} onClick={() => setRosterSave(true)}
            data-testid="month-roster-save">
            {t(`Save ${Object.keys(rosterChanges).length} change(s)`, `حفظ ${Object.keys(rosterChanges).length} تغيير`)}
          </Button>
        </div>
      )}

      {days.length > 0 && (
        <div className="grid grid-cols-7 gap-1.5" data-testid="month-calendar">
          {(isRTL ? WEEKDAYS.ar : WEEKDAYS.en).map((w) => <div key={w} className="text-xs text-center text-slate-500">{w}</div>)}
          {Array.from({ length: lead }).map((_, i) => <div key={`lead-${i}`} />)}
          {days.map((d) => {
            const rc = rosterChanges[d.day];
            const st = STATUS[d.status] || {};
            const open = (sugByDay[d.day] || []).filter((s) => s.status === 'open').length;
            return (
              <button key={d.day} onClick={() => clickDay(d)} data-testid={`month-day-${d.day}`}
                className={`text-start border rounded-md p-1.5 min-h-[76px] ${st.cls || ''} ${rc ? 'ring-2 ring-brand-orange' : ''}`}>
                <div className="flex justify-between text-xs">
                  <b>{Number(d.day.slice(8))}</b>
                  {open > 0 && <span className="text-amber-700">●{open}</span>}
                </div>
                <div className="text-[11px] leading-tight mt-1">{rc ? (rc === 'off' ? t('→ off', '← راحة') : t('→ work', '← عمل')) : label(STATUS, d.status, isRTL)}</div>
                {d.worked_minutes > 0 && <div className="text-[11px]">{hm(d.worked_minutes)}{d.overtime_minutes > 0 ? ` +${hm(d.overtime_minutes)}` : ''}</div>}
              </button>
            );
          })}
        </div>
      )}

      {/* Day detail */}
      <Dialog open={!!day} onOpenChange={(v) => { if (!v) setOpenDay(null); }}>
        <DialogContent className="max-w-2xl" dir={isRTL ? 'rtl' : 'ltr'} data-testid="month-day-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {day?.day} <Badge variant="outline">{label(STATUS, day?.status, isRTL)}</Badge>
              {day?.holiday_name && <Badge variant="secondary">{day.holiday_name}</Badge>}
            </DialogTitle>
          </DialogHeader>
          {day && (
            <div className="space-y-3 text-sm">
              <div>
                <div className="font-semibold mb-1">{t('Punches (raw, never deleted)', 'البصمات (الأصلية، لا تُحذف)')}</div>
                <div className="flex flex-wrap gap-1.5">
                  {day.punches.length ? day.punches.map((p) => (
                    <span key={p} className={`border rounded px-1.5 py-0.5 ${day.ignored_punches.includes(p) ? 'line-through text-slate-400'
                      : day.duplicate_punches.includes(p) ? 'text-slate-400' : ''}`}>{clock(p)}</span>
                  )) : <span className="text-slate-500">{t('none', 'لا يوجد')}</span>}
                </div>
              </div>
              <div>
                <div className="font-semibold mb-1">{t('Shifts counted', 'الفترات المحتسبة')}</div>
                {day.segments.map((s, i) => (
                  <div key={i}>{clock(s.start)} → {clock(s.end)} {s.source !== 'punch' && <Badge variant="outline" className="ms-1">{s.source}</Badge>}</div>
                ))}
                <div className="text-slate-600 mt-1">
                  {t('Worked', 'العمل')} {hm(day.worked_minutes)} · {t('base', 'الأساس')} {hm(day.base_minutes)} · {t('overtime', 'إضافي')} {hm(day.overtime_minutes)}
                  {day.late_minutes > 0 && ` · ${t('late', 'تأخير')} ${hm(day.late_minutes)}`}
                </div>
              </div>
              {(sugByDay[day.day] || []).length > 0 && (
                <div>
                  <div className="font-semibold mb-1">{t('Questions', 'الأسئلة')}</div>
                  {(sugByDay[day.day] || []).map((s) => (
                    <div key={s.id}>{label(SUGGESTION, s.type, isRTL)} — {s.status}{s.action_key ? ` (${label(ACTION, s.action_key, isRTL)})` : ''}</div>
                  ))}
                </div>
              )}
              <div>
                <div className="font-semibold mb-1">{t('Changes', 'التعديلات')}</div>
                {!dayAdjustments.length && <div className="text-slate-500">{t('none', 'لا يوجد')}</div>}
                {dayAdjustments.map((a) => (
                  <div key={a.id} className={`flex items-center gap-2 ${a.undone ? 'text-slate-400 line-through' : ''}`}>
                    <span>{FIX_LABEL[a.kind] ? label(FIX_LABEL, a.kind, isRTL)
                      : a.action_key ? label(ACTION, a.action_key, isRTL) : a.kind === 'dismiss' ? t('Dismissed', 'استبعاد') : label(ACTION, a.kind, isRTL)}{a.time ? ` ${clock(a.time)}` : ''}{a.status ? ` → ${label(STATUS, a.status, isRTL)}` : ''}</span>
                    <span className="text-xs text-slate-500">“{a.reason}”</span>
                    {!a.undone && (
                      <Button size="sm" variant="ghost" onClick={() => setUndoing(a)} data-testid="month-undo">
                        <Undo2 className="h-4 w-4 me-1" />{t('Undo', 'تراجع')}
                      </Button>
                    )}
                  </div>
                ))}
              </div>
              {access?.hr_scope && (
                <Button size="sm" variant="outline" onClick={() => setFix({ kind: 'add_punch' })} data-testid="month-fix">
                  <Wrench className="h-4 w-4 me-1" />{t('Fix this day', 'تصحيح هذا اليوم')}
                </Button>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <ReasonDialog open={!!fix} isRTL={isRTL} title={t('Fix the day', 'تصحيح اليوم')}
        description={t('The original punches stay; this change is logged and can be undone.', 'تبقى البصمات الأصلية؛ يُسجَّل التعديل ويمكن التراجع عنه.')}
        onCancel={() => setFix(null)} onConfirm={saveFix}>
        {fix && (
          <div className="grid grid-cols-2 gap-2 text-sm">
            <select className="border rounded-md px-2 py-2 col-span-2" value={fix.kind} onChange={(e) => setFix({ kind: e.target.value })}
              data-testid="fix-kind">
              {FIX_KINDS.map((k) => <option key={k} value={k}>{label(FIX_LABEL, k, isRTL)}</option>)}
            </select>
            {['add_punch', 'ignore_punch'].includes(fix.kind) && (<>
              <Input type="time" value={fix.time || ''} onChange={(e) => setFix({ ...fix, time: e.target.value })} data-testid="fix-time" />
              <label className="flex items-center gap-1"><input type="checkbox" checked={!!fix.next_day}
                onChange={(e) => setFix({ ...fix, next_day: e.target.checked })} />{t('after midnight', 'بعد منتصف الليل')}</label>
            </>)}
            {fix.kind === 'set_status' && (
              <select className="border rounded-md px-2 py-2 col-span-2" value={fix.status || ''} onChange={(e) => setFix({ ...fix, status: e.target.value })}>
                <option value="">—</option>
                {SET_STATUSES.map((s) => <option key={s} value={s}>{label(STATUS, s, isRTL)}</option>)}
              </select>
            )}
            {fix.kind === 'split' && (<>
              <Input type="time" value={fix.break_start || ''} onChange={(e) => setFix({ ...fix, break_start: e.target.value })} />
              <Input type="time" value={fix.break_end || ''} onChange={(e) => setFix({ ...fix, break_end: e.target.value })} />
            </>)}
            {fix.kind === 'approve_overtime' && (
              <Input type="number" min="0" value={fix.minutes || ''} onChange={(e) => setFix({ ...fix, minutes: e.target.value })}
                placeholder={t('minutes', 'دقائق')} />
            )}
          </div>
        )}
      </ReasonDialog>

      <ReasonDialog open={!!undoing} isRTL={isRTL} destructive title={t('Undo this change', 'التراجع عن هذا التعديل')}
        onCancel={() => setUndoing(null)} onConfirm={undo} />
      <ReasonDialog open={rosterSave} isRTL={isRTL} title={t('Save off days', 'حفظ أيام الراحة')}
        onCancel={() => setRosterSave(false)} onConfirm={saveRoster} />
      <ReasonDialog open={!!profile} isRTL={isRTL} title={t('Work schedule profile', 'ملف جدول الدوام')}
        description={t('Decides how hours count (D4). Planned shifts are used for late / early.', 'يحدد طريقة احتساب الساعات. تُستخدم الفترات المخططة لحساب التأخير والانصراف المبكر.')}
        onCancel={() => setProfile(null)} onConfirm={saveProfile}>
        {profile && (
          <div className="space-y-2 text-sm">
            <select className="border rounded-md px-2 py-2 w-full" value={profile.key} onChange={(e) => setProfile({ ...profile, key: e.target.value })}
              data-testid="profile-key">
              {Object.keys(PROFILES).map((k) => <option key={k} value={k}>{label(PROFILES, k, isRTL)}</option>)}
            </select>
            {profile.shifts.map(([a, b], i) => (
              <div key={i} className="flex items-center gap-2">
                <span className="w-16">{t(`Shift ${i + 1}`, `الفترة ${i + 1}`)}</span>
                <Input type="time" value={a} onChange={(e) => { const s = [...profile.shifts]; s[i] = [e.target.value, b]; setProfile({ ...profile, shifts: s }); }} />
                <Input type="time" value={b} onChange={(e) => { const s = [...profile.shifts]; s[i] = [a, e.target.value]; setProfile({ ...profile, shifts: s }); }} />
              </div>
            ))}
          </div>
        )}
      </ReasonDialog>
    </div>
  );
}
